import { useQuery } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';

import { BizyUserConversation, BizyUserConversationDetail, BizyUserMessage } from '../schemas/bizy-conversations';
import { listOf } from '../schemas/utils';
import { headers } from './meta';

// ─── Query Keys ──────────────────────────────────────────────────────────────

enum QueryKey {
	Conversations = 'bizy-user-conversations',
	Conversation = 'bizy-user-conversation',
}

export const bizyUserConversationsQueryKey = (params?: Record<string, unknown>) => [QueryKey.Conversations, params].filter(Boolean);

export const bizyUserConversationQueryKey = (id?: string) => [QueryKey.Conversation, id].filter(Boolean);

// ─── Path helpers ────────────────────────────────────────────────────────────

export const bizyConversationsPath = (prefix = '') => `${prefix}/bizy/conversations`;
export const bizyConversationPath = (id: string, prefix = '') => `${prefix}/bizy/conversations/${id}`;

// ─── API response types ──────────────────────────────────────────────────────

interface BizyConversationsIndexAPIResponse {
	conversations: Array<Record<string, unknown>>;
	page: number;
	perPage: number;
	totalCount: number;
	totalPages: number;
}

interface BizyConversationDetailAPIResponse {
	conversation: {
		conversationId: string;
		pagePath: string | null;
		messages: Array<Record<string, unknown>>;
	};
}

// ─── Pagination metadata ─────────────────────────────────────────────────────

export class BizyConversationsMeta {
	page = 1;
	perPage = 25;
	totalCount = 0;
	totalPages = 0;

	constructor(data: Partial<BizyConversationsMeta> = {}) {
		Object.assign(this, data);
	}
}

// ─── Query params ────────────────────────────────────────────────────────────

export interface BizyConversationsParams {
	page?: number;
	per_page?: number;
}

// ─── Query hooks ─────────────────────────────────────────────────────────────

/**
 * Fetches paginated list of the current user's Bizy conversations.
 *
 * GET /api/v2/bizy/conversations
 */
export const useBizyUserConversations = (params?: BizyConversationsParams) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: bizyUserConversationsQueryKey(params as Record<string, unknown>),
		queryFn: async () => {
			const res = await http.get<BizyConversationsIndexAPIResponse>(bizyConversationsPath(), {
				params,
				headers,
			});

			return [
				listOf(BizyUserConversation).parse(res.data.conversations),
				new BizyConversationsMeta({
					page: res.data.page,
					perPage: res.data.perPage,
					totalCount: res.data.totalCount,
					totalPages: res.data.totalPages,
				}),
			] as const;
		},
		keepPreviousData: true,
	});
};

/**
 * Fetches full conversation detail with all messages.
 *
 * GET /api/v2/bizy/conversations/:id
 */
export const useBizyUserConversation = (conversationId: string | undefined) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: bizyUserConversationQueryKey(conversationId),
		queryFn: async () => {
			// conversationId is guaranteed by `enabled: !!conversationId` below
			const res = await http.get<BizyConversationDetailAPIResponse>(bizyConversationPath(conversationId as string), {
				headers,
			});

			const conv = res.data.conversation;

			return {
				conversationId: conv.conversationId,
				pagePath: conv.pagePath,
				messages: listOf(BizyUserMessage).parse(conv.messages),
			} as BizyUserConversationDetail;
		},
		enabled: !!conversationId,
	});
};
